
import { useEffect } from 'react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsModal({ isOpen, onClose }: TermsModalProps) {
  // ESC tuşu ile kapatma
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-gray-200/50 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Kullanım Şartları ve Gizlilik Politikası
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100/80 hover:bg-gray-200/80 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            title="Kapat"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Son güncelleme:</strong> 1 Kasım 2025
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              Bu web sitesi www.guventar.com, GüvenTar tarafından işletilmektedir. Siteye erişerek veya kullanarak aşağıdaki şartları kabul etmiş sayılırsınız. Bu şartları kabul etmiyorsanız lütfen Siteyi kullanmayınız.
            </p>

            <div className="space-y-8">
              {/* 1. Genel Hükümler */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  Genel Hükümler
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Siteye erişim sağlayan her kullanıcı, burada belirtilen koşulları okumuş, anlamış ve kabul etmiş sayılır.</p>
                  <p>Firma, dilediği zaman bu şartlarda değişiklik yapma hakkını saklı tutar. Değişiklikler, Site üzerinde yayımlandığı andan itibaren geçerlidir.</p>
                </div>
              </section>

              {/* 2. Hizmetin Kullanımı */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Hizmetin Kullanımı
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Site yalnızca yasal amaçlarla kullanılabilir.</p>
                  <p>Kullanıcı, Site üzerinde yürürlükteki yasaları ihlal eden, üçüncü şahısların haklarını zedeleyen veya kötüye kullanım teşkil eden hiçbir işlemde bulunamaz.</p>
                  <p>Site içeriğinin (metin, görsel, logo, tasarım, yazılım vb.) izinsiz kopyalanması, çoğaltılması veya dağıtılması yasaktır.</p>
                </div>
              </section>

              {/* 3. Üyelik ve Güvenlik */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Üyelik ve Güvenlik
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Üyelik veya form işlemleri sırasında sağlanan tüm bilgiler doğru ve güncel olmalıdır.</p>
                  <p>Kullanıcı, hesap bilgilerini (kullanıcı adı, şifre vb.) gizli tutmakla yükümlüdür.</p>
                  <p>Hesap bilgilerinin izinsiz kullanımından doğacak sorumluluk kullanıcıya aittir.</p>
                </div>
              </section>

              {/* 4. Gizlilik Politikası */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Gizlilik Politikası
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Kullanıcıların kişisel verileri, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) hükümlerine uygun olarak işlenir ve saklanır.</p>
                  <p>Toplanan kişisel veriler yalnızca hizmet sunmak, iletişim kurmak ve yasal yükümlülüklerin yerine getirilmesi amacıyla kullanılacaktır.</p>
                  <p>Kişisel verileriniz, açık rızanız olmaksızın üçüncü kişilerle paylaşılmaz.</p>
                  <p>Kullanıcılar, kişisel verilerine erişme, düzeltme veya silme talebinde bulunma hakkına sahiptir.</p>
                </div>
              </section>

              {/* 5. Sorumluluk Reddi */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                  Sorumluluk Reddi
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Sitede yer alan içerikler genel bilgilendirme amaçlıdır.</p>
                  <p>Firma, sitedeki bilgilerin doğruluğu veya güncelliği konusunda garanti vermez.</p>
                  <p>Kullanıcı, Siteyi kendi sorumluluğunda kullanır.</p>
                </div>
              </section>

              {/* 6. Fikri Mülkiyet Hakları */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">6</span>
                  Fikri Mülkiyet Hakları
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Sitedeki tüm içeriklerin (metin, görsel, logo, tasarım, yazılım vb.) telif hakkı GüvenTar'a aittir.</p>
                  <p>İzinsiz kopyalama, çoğaltma, paylaşma veya değiştirme yasaktır.</p>
                </div>
              </section>

              {/* 7. Uygulanacak Hukuk ve Yetki */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">7</span>
                  Uygulanacak Hukuk ve Yetki
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Bu şartlar Türkiye Cumhuriyeti yasalarına tabidir.</p>
                  <p>Taraflar arasında doğabilecek uyuşmazlıklarda İstanbul (Anadolu) Mahkemeleri ve İcra Daireleri yetkilidir.</p>
                </div>
              </section>

              {/* 8. İletişim */}
              <section>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">8</span>
                  İletişim
                </h3>
                <div className="space-y-3 text-gray-700 leading-relaxed">
                  <p>Her türlü soru, görüş veya talep için bizimle iletişime geçebilirsiniz:</p>
                  <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 mt-4">
                    <div className="space-y-2">
                      <p><strong>GüvenTar</strong></p>
                      <p className="flex items-center">
                        <i className="ri-mail-line text-green-600 mr-2"></i>
                        <strong>E-posta:</strong> bilgi@guventar.com
                      </p>
                      <p className="flex items-start">
                        <i className="ri-map-pin-line text-green-600 mr-2 mt-0.5"></i>
                        <span><strong>Adres:</strong> Cumalı Mh. Antalya Cd. No:9 Serik/Antalya</span>
                      </p>
                      <p className="flex items-center">
                        <i className="ri-phone-line text-green-600 mr-2"></i>
                        <strong>Telefon:</strong> +90 553 414 52 63
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-md border-t border-gray-200/50 px-6 py-4">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-2"></i>
              Anladım
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
